import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface ControlItem {
  id: number;
  title: string;
  description: string;
  dueDate: string;
  assignedTo: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'inProgress' | 'completed';
  completedDate?: string;
}

@Component({
  selector: 'app-control-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './control-list.component.html',
  styleUrl: './control-list.component.scss'
})
export class ControlListComponent implements OnInit {
  controlItems: ControlItem[] = [];

  // 篩選和排序選項
  filterStatus: string = 'all';
  sortBy: string = 'dueDate';

  // 新增項目的表單
  showAddForm: boolean = false;
  newItem: ControlItem = {
    id: 0,
    title: '',
    description: '',
    dueDate: '',
    assignedTo: '',
    priority: 'medium',
    status: 'pending'
  };

  // 編輯項目
  editingItem: ControlItem | null = null;

  ngOnInit() {
    this.loadControlItems();
  }

  // 載入控制清單項目（模擬資料）
  loadControlItems() {
    // 在實際應用中，這裡應該從服務或API獲取數據
    this.controlItems = [
      {
        id: 1,
        title: '公共危險品申報',
        description: '依法規要求完成每季公共危險品的申報作業',
        dueDate: '2025-05-15',
        assignedTo: '李安全',
        priority: 'high',
        status: 'pending'
      },
      {
        id: 2,
        title: 'SDS文件建置',
        description: '完成所有化學品的SDS文件建置和更新',
        dueDate: '2025-06-30',
        assignedTo: '張工程',
        priority: 'medium',
        status: 'inProgress'
      },
      {
        id: 3,
        title: '內部安全稽核',
        description: '進行季度內部安全稽核，確保符合法規要求',
        dueDate: '2025-05-30',
        assignedTo: '王經理',
        priority: 'medium',
        status: 'pending'
      },
      {
        id: 4,
        title: '化學品分類標示',
        description: '依照GHS系統完成所有化學品的分類和標示',
        dueDate: '2025-04-20',
        assignedTo: '林專員',
        priority: 'high',
        status: 'completed',
        completedDate: '2025-04-18'
      }
    ];
  }

  // 過濾項目
  getFilteredItems() {
    let items = [...this.controlItems];

    // 按狀態過濾
    if (this.filterStatus !== 'all') {
      items = items.filter(item => item.status === this.filterStatus);
    }

    // 排序
    items.sort((a, b) => {
      if (this.sortBy === 'dueDate') {
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      } else if (this.sortBy === 'priority') {
        const priorityValue = { low: 0, medium: 1, high: 2 };
        return priorityValue[b.priority] - priorityValue[a.priority];
      }
      return 0;
    });

    return items;
  }

  // 開啟新增表單
  openAddForm() {
    this.showAddForm = true;
    this.newItem = {
      id: this.getNextId(),
      title: '',
      description: '',
      dueDate: '',
      assignedTo: '',
      priority: 'medium',
      status: 'pending'
    };
  }

  // 取消新增
  cancelAdd() {
    this.showAddForm = false;
  }

  // 提交新項目
  submitNewItem() {
    if (!this.newItem.title || !this.newItem.dueDate) {
      alert('請填寫必填欄位（標題和截止日期）');
      return;
    }

    this.controlItems.push({ ...this.newItem });
    this.showAddForm = false;

    // 在實際應用中，這裡應該調用服務將數據保存到後端
  }

  // 獲取下一個ID
  private getNextId(): number {
    return this.controlItems.length > 0
      ? Math.max(...this.controlItems.map(item => item.id)) + 1
      : 1;
  }

  // 編輯項目
  editItem(item: ControlItem) {
    this.editingItem = { ...item };
  }

  // 取消編輯
  cancelEdit() {
    this.editingItem = null;
  }

  // 保存編輯
  saveEdit() {
    if (!this.editingItem) return;

    const index = this.controlItems.findIndex(item => item.id === this.editingItem!.id);
    if (index !== -1) {
      this.controlItems[index] = { ...this.editingItem };
    }

    this.editingItem = null;

    // 在實際應用中，這裡應該調用服務將更新保存到後端
  }

  // 刪除項目
  deleteItem(id: number) {
    if (confirm('確定要刪除此項目嗎？')) {
      this.controlItems = this.controlItems.filter(item => item.id !== id);

      // 在實際應用中，這裡應該調用服務將刪除操作提交到後端
    }
  }

  // 更改項目狀態
  changeStatus(item: ControlItem, status: 'pending' | 'inProgress' | 'completed') {
    const index = this.controlItems.findIndex(i => i.id === item.id);
    if (index !== -1) {
      this.controlItems[index].status = status;

      // 如果標記為已完成，添加完成日期
      if (status === 'completed') {
        this.controlItems[index].completedDate = new Date().toISOString().split('T')[0];
      } else {
        delete this.controlItems[index].completedDate;
      }

      // 在實際應用中，這裡應該調用服務將狀態更新保存到後端
    }
  }

  // 獲取優先級標籤的CSS類
  getPriorityClass(priority: string): string {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return '';
    }
  }

  // 獲取狀態標籤的CSS類
  getStatusClass(status: string): string {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'inProgress': return 'status-inprogress';
      case 'completed': return 'status-completed';
      default: return '';
    }
  }
}
